<template>
  <div>
    <CampaignStats
      v-if="campaignKey && user.canAccessToolbar"
      :campaign-key="campaignKey"
    />

    <div v-if="buttonText">
      <button class="button is-primary" @click="openPostcardEditor">
        {{ buttonText }}
      </button>
    </div>

    <div>
      <Modal :modal-id="modalId">
        <div
          id="postcard-editor"
          class="relative flex h-full flex-col belowTablet:h-screen belowTablet:pt-size-3"
        >
          <h2 class="text-2xl font-bold">
            {{ currentStep?.title }}
          </h2>
          <p class="text-grey-dark dark:text-white">
            {{ currentStep?.description }}
          </p>

          <div
            v-if="error"
            class="message is-danger my-size-6 flex-1 overflow-y-auto"
          >
            <div class="message-body">
              {{ error }}
            </div>
          </div>

          <div ref="stepContainerRef" class="postcard-step-container">
            <Transition name="postcard-fade">
              <div :key="currentStep?.name" class="postcard-step">
                <component :is="currentStepComponent" />
              </div>
            </Transition>
          </div>

          <div
            id="step-navigation"
            :class="{
              '!justify-end': currentStepIndex === steps.length - 1,
            }"
          >
            <div
              v-if="currentStepIndex < steps.length - 1"
              class="self-end text-grey-dark belowTablet:hidden dark:text-white"
            >
              {{ $texts('postcard.step') }}
              {{ currentStepIndex + 1 }} {{ $texts('postcard.of') }}
              {{ steps.length - 1 }}
            </div>
            <div class="flex gap-2 belowTablet:w-full">
              <button
                v-if="currentStepIndex < steps.length - 1"
                class="button is-secondary belowTablet:flex-1 belowTablet:justify-center"
                :disabled="isLoading"
                @click="previousStep"
              >
                {{ steps[currentStepIndex]?.buttonTextBackward }}
              </button>

              <button
                class="button is-primary belowTablet:flex-1 belowTablet:justify-center"
                :disabled="
                  isLoading ||
                  (currentStepIndex === steps.length - 2 && isApproved)
                "
                @click="handlePrimaryAction"
              >
                <span>{{ steps[currentStepIndex]?.buttonTextForward }}</span>
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  </div>
</template>

<script setup lang="ts">
import Modal from '~~/layers/postcard/app/components/Modal/index.vue'
import CampaignStats from '~~/layers/postcard/app/components/CampaignStats/index.vue'
import type {
  PostcardDesignOption,
  PostcardStep,
} from '~~/layers/postcard/app/types'
import { onKeyStroke } from '@vueuse/core'
import { DEFAULT_QR_BRANDING } from '~~/layers/postcard/app/utils/postcardBranding'

const {
  setCampaignKey,
  setCampaignText,
  setQrLink,
  setStampImageUrl,
  setWebformId,
  setDesigns,
  setBranding,
  executeStepLogic,
  isLoading,
  error,
  isApproved,
} = useCampaignData()
const user = await useDrupalUser()
const { openModal, modalId, closeModal } = useModal('postcard')
const { trackEvent } = useAnalytics()
const { $texts } = useEasyTexts()

const props = withDefaults(
  defineProps<{
    campaignKey?: string | null
    campaignText?: string | null
    qrLink?: string | null
    stampImageUrl?: string | null
    webformId?: string | null
    buttonText?: string | null
    designs?: PostcardDesignOption[] | null
  }>(),
  {
    campaignKey: '',
    campaignText: '',
    qrLink: '',
    stampImageUrl: '',
    webformId: '',
    buttonText: '',
    designs: () => [],
  },
)

const accompanyingText = computed(() => (props.campaignText ?? '').trimEnd())

const steps: PostcardStep[] = [
  {
    name: '10-design',
    title: $texts('postcard.stepDesignTitle'),
    buttonText: $texts('postcard.stepDesignButtonText'),
    buttonTextBackward: $texts('postcard.stepDesignButtonBackwardText'),
    buttonTextForward: $texts('postcard.stepDesignButtonForwardText'),
    description: $texts('postcard.stepDesignDescription'),
    onNext: async () => executeStepLogic('10-design'),
  },
  {
    name: '20-receiving',
    title: $texts('postcard.stepReceivingTitle'),
    buttonText: $texts('postcard.stepReceivingButtonText'),
    buttonTextBackward: $texts('postcard.stepReceivingButtonBackwardText'),
    buttonTextForward: $texts('postcard.stepReceivingButtonForwardText'),
    description: $texts('postcard.stepReceivingDescription'),
    onNext: async () => executeStepLogic('20-receiving'),
  },
  {
    name: '30-message',
    title: $texts('postcard.stepMessageTitle'),
    buttonText: $texts('postcard.stepMessageButtonText'),
    buttonTextBackward: $texts('postcard.stepMessageButtonBackwardText'),
    buttonTextForward: $texts('postcard.stepMessageButtonForwardText'),
    description: $texts('postcard.stepMessageDescription'),
    onNext: async () => executeStepLogic('30-message'),
  },
  {
    name: '40-sender',
    title: $texts('postcard.stepSenderTitle'),
    buttonText: $texts('postcard.stepSenderButtonText'),
    buttonTextBackward: $texts('postcard.stepSenderButtonBackwardText'),
    buttonTextForward: $texts('postcard.stepSenderButtonForwardText'),
    description: $texts('postcard.stepSenderDescription'),
    onNext: async () => executeStepLogic('40-sender'),
  },
  {
    name: '50-review',
    title: $texts('postcard.stepReviewTitle'),
    buttonText: $texts('postcard.stepReviewButtonText'),
    buttonTextBackward: $texts('postcard.stepReviewButtonBackwardText'),
    buttonTextForward: $texts('postcard.stepReviewButtonForwardText'),
    description: $texts('postcard.stepReviewDescription'),
    onNext: async () => executeStepLogic('50-review'),
  },
  {
    name: '60-confirmation',
    title: $texts('postcard.stepConfirmationTitle'),
    buttonText: $texts('postcard.stepConfirmationButtonText'),
    buttonTextBackward: $texts('postcard.stepConfirmationButtonBackwardText'),
    buttonTextForward: $texts('postcard.stepConfirmationButtonForwardText'),
    description: $texts('postcard.stepConfirmationDescription'),
  },
]
const currentStep = ref(steps[0])

const currentStepComponent = computed(() => {
  const stepName = currentStep.value?.name
  return defineAsyncComponent(
    () =>
      import(`~~/layers/postcard/app/components/Steps/${stepName}/index.vue`),
  )
})

const currentStepIndex = computed(() =>
  steps.findIndex((step) => step.name === currentStep.value?.name),
)

const stepContainerRef = ref<HTMLElement | null>(null)

function setFocus() {
  if (!stepContainerRef.value) return
  const firstInput = stepContainerRef.value.querySelector<HTMLElement>(
    'input:not([type="hidden"]):not([disabled]), textarea:not([disabled]), select:not([disabled]), button:not([disabled])',
  )
  firstInput?.focus()
}

async function nextStep() {
  // Call the current step's onNext function if it exists
  const currentStepData = currentStep.value
  if (currentStepData && currentStepData.onNext) {
    const canProceed = await currentStepData.onNext()
    if (!canProceed) {
      console.log('Cannot proceed to next step - validation failed')
      return
    }
  }

  const currentIndex = currentStepIndex.value
  if (currentIndex < steps.length - 1) {
    currentStep.value = steps[currentIndex + 1]
  }
}

async function handlePrimaryAction() {
  const index = currentStepIndex.value
  // Second to last step (review): trigger nextStep which will run validation/onNext (approval happens there)
  if (index === steps.length - 2) {
    await nextStep()
    return
  }
  // Last step (confirmation): close modal
  if (index === steps.length - 1) {
    closeModal()
    setTimeout(() => {
      currentStep.value = steps[0] // reset to first step
    }, 500)
    return
  }
  // Default: proceed normally
  await nextStep()
}

function previousStep() {
  const currentIndex = currentStepIndex.value
  if (currentIndex === 0) {
    closeModal()
    return
  }
  if (currentIndex > 0) {
    currentStep.value = steps[currentIndex - 1]
  }
}

async function openPostcardEditor() {
  // Re-apply campaign props when opening so HMR / stale client state cannot
  // leave webformId (and related fields) empty mid-flow.
  setCampaignKey(props.campaignKey ?? '')
  setCampaignText(accompanyingText.value)
  setQrLink(props.qrLink ?? '')
  setStampImageUrl(props.stampImageUrl ?? '')
  setWebformId(props.webformId ?? '')
  setDesigns(props.designs ?? [])
  setBranding({
    brandingQRCode: {
      encodedText: props.qrLink ?? '',
      accompanyingText: accompanyingText.value,
      ...DEFAULT_QR_BRANDING,
    },
  })
  console.log('starting postcard editor')
  trackEvent('start_postcard_contest')
  openModal()
}

// Props come from the GraphQL paragraph payload once on load.
setCampaignKey(props.campaignKey ?? '')
setCampaignText(accompanyingText.value)
setQrLink(props.qrLink ?? '')
setStampImageUrl(props.stampImageUrl ?? '')
setWebformId(props.webformId ?? '')
setDesigns(props.designs ?? [])
setBranding({
  brandingQRCode: {
    encodedText: props.qrLink ?? '',
    accompanyingText: accompanyingText.value,
    ...DEFAULT_QR_BRANDING,
  },
})

watch(currentStep, async () => {
  await nextTick()
  // Wait for transition to complete (200ms as defined in CSS)
  setTimeout(() => {
    setFocus()
  }, 250)
})

onKeyStroke(['Escape'], () => closeModal())
</script>

<style lang="postcss">
#postcard-editor {
  .formkit-form {
    @apply mt-size-3 grid gap-x-size-6;
  }

  .formkit-help {
    @apply text-grey-dark dark:text-white;
  }
}
.postcard-step-container {
  @apply relative flex-1;
}

.postcard-step {
  @apply block h-full w-full;
}

.postcard-fade-enter-active,
.postcard-fade-leave-active {
  @apply transition-opacity duration-200 ease-bounce;
}
/* Keep the outgoing step from pushing the layout while it fades out. */
.postcard-fade-leave-active {
  @apply absolute inset-0;
}
.postcard-fade-enter-from,
.postcard-fade-leave-to {
  opacity: 0;
}

.postcard-fade-leave-from,
.postcard-fade-enter-to {
  opacity: 1;
}

#step-navigation {
  @apply sticky bottom-0 z-100 mt-auto flex justify-between bg-background pb-size-4 pt-size-6 belowTablet:px-size-6 tablet:pb-0;
}

@screen belowTablet {
  #step-navigation {
    @apply -mx-size-6;
  }
}
</style>
